import { computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import moment from 'moment';
import { useI18n } from '@/composables';
import { usePeriodPickerRange } from '@/composables/usePeriodPickerRange';
import { useStore } from '@/store';
import { UiDatePickerDate } from '@/components/ui/ui-date-picker.vue';

const getTimeFormatted = (num: number) => (num < 10 ? `0${num}` : num);

const TIMES = Array(25)
  .fill(null)
  .map((_, index) => `${getTimeFormatted(index)}:00`);

const splitTime = (time: string) => {
  const [hours, minutes] = time.split(':');
  const offset = +minutes <= 30 ? 0 : 1;
  return +hours + offset;
};

export const useUiPeriodPicker = (
  props: {
    text?: string;
    dateValue?: string;
    columns?: number;
    value: TimeRange;
    format?: string;
    timeInTitle?: string | boolean;
    menuContentClass?: string;
    showTimezone?: boolean;
  },
  onInput: (value: TimeRange) => void,
) => {
  const { t } = useI18n();
  const store = useStore();
  const { mdAndUp } = useDisplay();
  const periodPickerRange = usePeriodPickerRange(props.format || 'YYYY-MM-DD HH:mm:ss');

  const picker = ref<{ toggleMenu?: (open: boolean) => void; redrawMenu?: () => void } | null>(
    null,
  );
  const datePicker = ref<{ adjustPageRange?: () => void } | null>(null);
  const drag = ref<UiDatePickerDate>(null);
  const syncingFromProps = ref(false);

  const range = ref<TimeRange>({
    from: '',
    to: '',
    timezone: store.user.defaultTimezone,
  });

  const emitRange = () => {
    onInput({ ...range.value });
  };

  const isFullWidth = mdAndUp;

  const pickerColumns = computed(() => props.columns || (isFullWidth.value ? 2 : 1));

  const start = computed(() => TIMES.slice(0, TIMES.length - 1));

  const isSameDay = computed(() =>
    moment(range.value.from).isSame(range.value.to, 'day'),
  );

  const isStartAfterEndTime = computed(() =>
    moment(range.value.from).isAfter(moment(range.value.to)),
  );

  const dateFrom = computed(() => moment(range.value.from).format('DD MMM YYYY'));

  const dateTo = computed(() => moment(range.value.to).format('DD MMM YYYY'));

  const timeFrom = computed({
    get: () => moment(range.value.from).format('HH:mm'),
    set: (value: string) => {
      const date = getDateWithUpdatedTime(range.value.from, value);
      range.value = {
        ...range.value,
        from: `${date}:00`,
      };

      if (isStartAfterEndTime.value) {
        timeTo.value = '24:00';
      }
      emitRange();
    },
  });

  const timeTo = computed({
    get: () => {
      const time = moment(range.value.to).add(1, 'seconds').format('HH');
      return time === '00' ? '24:00' : `${time}:00`;
    },
    set: (value: string) => {
      range.value = {
        ...range.value,
        to: moment(getDateWithUpdatedTime(range.value.to, value))
          .subtract(1, 'seconds')
          .format('YYYY-MM-DD HH:mm:ss'),
      };
      emitRange();
    },
  });

  const end = computed(() => {
    const startTime = splitTime(timeFrom.value);
    if (isSameDay.value) {
      return TIMES.slice(1).filter((time) => splitTime(time) > startTime);
    }
    return TIMES.slice(1, TIMES.length);
  });

  const pMenuContentClass = computed(() => {
    const classes = ['ui-period-picker__menu-wrapper'];
    if (props.menuContentClass) {
      classes.push(props.menuContentClass);
    }
    return classes.join(' ');
  });

  const isTimeInTitle = computed(
    () => props.timeInTitle === '' || props.timeInTitle,
  );

  const defaultText = computed(() =>
    isTimeInTitle.value
      ? `${dateFrom.value} ${timeFrom.value} – ${dateTo.value} ${timeTo.value}`
      : `${dateFrom.value} – ${dateTo.value}`,
  );

  const pText = computed(() => props.text || defaultText.value);

  const pRange = computed({
    get: (): UiDatePickerDate => ({
      start: moment(range.value.from).toDate(),
      end: moment(range.value.to).toDate(),
      timezone: range.value.timezone,
    }),
    set: (value: UiDatePickerDate) => {
      const from = moment(range.value.from);
      const to = moment(range.value.to);

      range.value = {
        ...range.value,
        from: moment(value.start)
          .hours(from.hours())
          .minutes(from.minutes())
          .seconds(from.seconds())
          .format(props.format || 'YYYY-MM-DD HH:mm:ss'),
        to: moment(value.end)
          .hours(to.hours())
          .minutes(to.minutes())
          .seconds(to.seconds())
          .format(props.format || 'YYYY-MM-DD HH:mm:ss'),
      };
      emitRange();
    },
  });

  const getDateWithUpdatedTime = (date: string, time: string) => {
    const dateWithoutTime = moment(date).format('YYYY-MM-DD');
    return `${dateWithoutTime} ${time}`;
  };

  const handleDrag = (value: UiDatePickerDate) => {
    if (value) {
      drag.value = value;
      return;
    }

    if (!value && drag.value) {
      range.value = {
        ...range.value,
        to: moment(drag.value.start)
          .hours(0)
          .minutes(0)
          .seconds(0)
          .format(props.format || 'YYYY-MM-DD HH:mm:ss'),
        from: moment(drag.value.end)
          .hours(23)
          .minutes(59)
          .seconds(59)
          .format(props.format || 'YYYY-MM-DD HH:mm:ss'),
      };
      drag.value = null;
      emitRange();
    }

    if (isSameDay.value && isStartAfterEndTime.value) {
      timeTo.value = '24:00';
    }
  };

  const toggleMenu = () => {
    picker.value?.toggleMenu?.(false);
  };

  const redrawMenu = () => {
    picker.value?.redrawMenu?.();
  };

  const adjustPageRange = () => {
    datePicker.value?.adjustPageRange?.();
  };

  watch(
    () => props.value,
    (value) => {
      syncingFromProps.value = true;
      range.value = { ...value };
      if (!range.value.timezone) {
        range.value.timezone = store.user.defaultTimezone;
      }
      syncingFromProps.value = false;
    },
    { immediate: true, deep: true },
  );

  watch(
    () => range.value.timezone,
    (value, oldValue) => {
      if (syncingFromProps.value) return;

      if (
        oldValue &&
        value &&
        (props.dateValue === 'last24hours' || props.dateValue === 'last48hours')
      ) {
        const rangeCollection = periodPickerRange.getRangeValueCollection({
          timezone: value,
        });
        const preset = rangeCollection[props.dateValue as keyof typeof rangeCollection];
        range.value = {
          ...range.value,
          from: preset.from,
          to: preset.to,
        };
      }
      emitRange();
    },
  );

  return {
    t,
    timezoneList: store.timezoneList,
    picker,    datePicker,
    range,
    pickerColumns,
    pMenuContentClass,
    pText,
    pRange,
    dateFrom,
    dateTo,
    timeFrom,
    timeTo,
    start,
    end,
    handleDrag,
    toggleMenu,
    redrawMenu,
    adjustPageRange,
  };
};
